import { Component, DOCUMENT, ElementRef, Input, PLATFORM_ID, inject, isDevMode } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ICON_IMG, ICON_SLUGS } from './icon-slugs';

/**
 * <app-icon src="/assets/icons/new_design/heart_up.svg"> o <app-icon name="close-outline">
 * (ionicons) — reemplazo ligero de <ion-icon>. ion-icon es un custom element de
 * Stencil: al registrarse, el navegador upgradea TODOS los ion-icon del DOM (shadow
 * DOM + fetch del SVG), lo que en el home eran 300+ upgrades = ~0,5s de hilo
 * principal (TBT).
 *
 * Si el icono es monocromo (está en ICON_REGISTRY) renderiza
 * <svg><use href="#mc-<slug>"></svg> contra el sprite único inyectado por
 * <app-icon-sprite> (ver app.component.html): ~60 B por uso, cero custom element,
 * cero shadow DOM, cero fetch, cero SVG repetido en el HTML.
 *
 * Los iconos CON color propio fijo (amazon_prime, gradientes, bluetick, logos
 * blancos sobre fondo de color...) NO pasan por el sprite: se dejan como <img>
 * (ICON_IMG), que también es de coste ~0 y no arriesga recolorearlos mal.
 */
@Component({
  // Dos formas del mismo componente:
  //   <app-icon src="...">      anfitrión + <svg> + <use>  = 3 nodos
  //   <svg appIcon src="...">   anfitrión(=svg) + <use>    = 2 nodos
  // La segunda es para donde el icono se repite mucho —las tarjetas del listado—, porque
  // ahí un nodo por icono son cientos: `app-chollo-buttons` pinta 4 iconos por tarjeta y
  // `app-velocidad` 2, o sea 120 de los 227 iconos de la portada. Sólo vale para iconos del
  // SPRITE; los que van como <img> (color propio) se quedan con `<app-icon>`.
  selector: 'app-icon, svg[appIcon]',
  template: `
    @if (anfitrionEsSvg) {
      <svg:use [attr.href]="href"></svg:use>
    } @else if (useSprite) {
      <svg class="app-icon__svg" aria-hidden="true" focusable="false"><use [attr.href]="href"></use></svg>
    } @else {
      <img class="app-icon__img" [src]="imgSrc" alt="" aria-hidden="true" loading="lazy" decoding="async">
    }
  `,
  // Cuando el anfitrión ES el <svg>, los dos atributos que antes llevaba el <svg> interno
  // tienen que ir al anfitrión. Se atan en vez de escribirlos en las 30 llamadas.
  host: {
    '[attr.aria-hidden]': "anfitrionEsSvg ? 'true' : null",
    '[attr.focusable]': "anfitrionEsSvg ? 'false' : null",
  },
  styleUrls: ['./icon.component.scss'],
  // Una tarjeta de chollo lleva 7 `app-icon` de los 10 componentes que tiene, y
  // con 520 tarjetas en el listado son ~3.600 instancias que la deteccion de
  // cambios por defecto recorreria en cada ciclo.
  //
  // MEDIDO: no cambia nada. 6,8 ms de hilo principal por tarjeta antes; 6,7 / 6,4
  // / 6,9 despues. La app usa zone.js, pero Ionic emite el scroll fuera de la zona
  // de Angular, asi que durante el scroll no hay ciclos que ahorrar. Se deja
  // puesto porque es correcto y gratis, no porque se haya medido una mejora.
  //
  // NO se ha hecho lo mismo con la tarjeta ni con `app-chollo-buttons`: ahi el
  // codigo muta el objeto del chollo en su sitio (`deal.liked = ...`) y con OnPush
  // esas mutaciones dejarian de refrescar la vista.
})
export class IconComponent {
  /** true cuando se ha usado como `<svg appIcon>` y el anfitrión ya ES el SVG. */
  readonly anfitrionEsSvg: boolean;

  constructor() {
    const el = inject(ElementRef).nativeElement as Element;
    this.anfitrionEsSvg = el.tagName.toLowerCase() === 'svg';
  }

  // Los dos `@Input() set` NO se migran a `input()`: el setter inyecta el <symbol> del
  // sprite ANTES del primer render, y con `computed` + `effect` ese efecto pasaría a
  // después, o sea un icono en blanco el primer ciclo. Está anotado en el tablero.
  useSprite = false;
  href = '';
  imgSrc = '';

  @Input()
  set src(value: string) {
    this.resolve(this.normalize(value), value || '');
  }

  /** Icono de ionicons por nombre, igual que <ion-icon name="close-outline">. */
  @Input()
  set name(value: string) {
    if (!value) {
      return;
    }
    const slug = `ionicons/${value}`;
    // Ruta de fallback silencioso si algún día ese ionicon no estuviera en el
    // registro: no hay asset físico servido para ionicons hoy (todos los usados
    // actualmente caen en ICON_REGISTRY), así que esta ruta es solo defensiva.
    this.resolve(slug, `/assets/icons/${slug}.svg`);
  }

  private document = inject(DOCUMENT);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private resolve(slug: string, fallbackSrc: string): void {
    if (ICON_SLUGS.has(slug)) {
      this.useSprite = true;
      this.href = `#mc-${slug}`;
      // El servidor poda del sprite los símbolos que la página SSR no usa (ahorra
      // ~40 KB de HTML por página). Un icono que aparezca después en cliente
      // (menú, modales, popovers...) puede no tener su <symbol>: se inyecta aquí
      // desde el registro (mismo contenido que genera el sprite). El registro se
      // carga con import() sólo en ese caso: son ~50 KB que antes iban en main.js
      // para todo el mundo, y lo normal es que el sprite del HTML ya traiga el
      // símbolo (lote 21). <use> resuelve el id cuando el símbolo aparece, así que
      // el icono se pinta en cuanto llega el trozo.
      if (this.isBrowser) void this.ensureSymbol(slug);
      return;
    }
    this.useSprite = false;
    this.imgSrc = fallbackSrc;
    if (isDevMode() && this.anfitrionEsSvg) {
      console.warn(
        `[app-icon] "${slug}" no está en el sprite: con <svg appIcon> no se pinta nada. ` +
          'Ese icono tiene que seguir siendo <app-icon>.'
      );
    }
    if (isDevMode() && !ICON_IMG.has(slug)) {
      // Slug ni en el sprite ni en la lista conocida de <img>: probablemente un
      // icono nuevo que aún no ha pasado por `node scripts/gen-icon-registry.mjs`.
      console.warn(
        `[app-icon] icono desconocido "${slug}" (${fallbackSrc}): no está en ICON_REGISTRY ni en ICON_IMG. ` +
          'Añádelo a una plantilla incluida en scripts/gen-icon-registry.mjs y regenera el registro.'
      );
    }
  }

  private async ensureSymbol(slug: string): Promise<void> {
    const id = `mc-${slug}`;
    const doc = this.document;
    if (!doc || !doc.getElementById || doc.getElementById(id)) return;
    const { ICON_REGISTRY } = await import('./icon-registry');
    // Dos iconos iguales en la misma vista piden el mismo símbolo a la vez: el segundo
    // en llegar se lo encuentra ya puesto.
    if (doc.getElementById(id)) return;
    let sprite: Element | null = doc.getElementById('mc-icon-sprite');
    if (!sprite) {
      sprite = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
      sprite.setAttribute('id', 'mc-icon-sprite');
      sprite.setAttribute('style', 'display:none');
      sprite.setAttribute('aria-hidden', 'true');
      doc.body.insertBefore(sprite, doc.body.firstChild);
    }
    const def = ICON_REGISTRY[slug];
    const symbol = doc.createElementNS('http://www.w3.org/2000/svg', 'symbol');
    symbol.setAttribute('id', id);
    symbol.setAttribute('viewBox', def.viewBox);
    symbol.innerHTML = def.content;
    sprite.appendChild(symbol);
  }

  /** '/assets/icons/new_design/heart_up.svg' -> 'new_design/heart_up' */
  private normalize(value: string): string {
    return (value || '').replace(/^.*\/assets\/icons\//, '').replace(/\.svg$/, '');
  }
}
